import { calcBillingRate, type EmployeeBillingData } from '@/lib/billing-rates-calc';
import { MONTH_NAMES_NB } from '@/lib/format';
import {
  capacitySlice,
  hoursByUserMonth,
  type CapacitySegment,
  type CapacitySlice,
  type CategoryHours,
} from '@/lib/capacity-breakdown';
import type { CapacityBreakdownData } from '@/hooks/use-capacity-breakdown';
import type { CapacityCategory } from '@/lib/capacity-breakdown';

/**
 * Ett nivå i sidepanelet. Nivåene stables, og «tilbake» går ett opp.
 * `months` avgrenser nivået til månedene det ble åpnet fra (for eksempel én
 * måned fra en søyle). Uten `months` gjelder hele perioden.
 */
export type TeamFaktView =
  | { kind: 'team' }
  | { kind: 'segment'; segment: CapacitySegment; months?: number[] }
  | { kind: 'month'; month: number }
  | { kind: 'person'; userId: string; months?: number[] }
  | { kind: 'person-ikke'; userId: string; months?: number[] }
  | { kind: 'person-venter'; userId: string; months?: number[] }
  | { kind: 'person-days'; userId: string; month: number }
  | {
      kind: 'entries';
      segment: CapacityCategory;
      months?: number[];
      userId?: string;
      projectId?: string;
      assignmentId?: string;
      /** Hva lista gjelder, til tittelen. */
      label: string;
    };

/** Månedene et nivå gjelder: utvalget det ble åpnet med, ellers hele perioden. */
export const monthsOf = (model: TeamFaktModel, months?: number[]) => months ?? model.months;

export interface TeamFaktModel {
  year: number;
  months: number[];
  target: number;
  employees: EmployeeBillingData[];
  byUserMonth: Map<string, CategoryHours>;
  breakdown: CapacityBreakdownData;
  team: CapacitySlice;
  billable: number;
  rate: number;
  sliceFor: (userIds: string[], months: number[]) => CapacitySlice;
  billableFor: (userId: string | null, months: number[]) => number;
  nameOf: (userId: string) => string;
}

/**
 * Setter sammen grad og kapasitet fra useBillingRates med førte timer fra
 * useCapacityBreakdown. Kapasiteten er den samme som i teamYtdRate, og
 * bare ansatte med kapasitet er med.
 */
export function buildTeamFaktModel(args: {
  year: number;
  throughMonth: number;
  target: number;
  teamRate: number;
  employees: EmployeeBillingData[];
  breakdown: CapacityBreakdownData;
}): TeamFaktModel {
  const employees = args.employees.filter((e) => e.monthlyCapacity > 0);
  const months = Array.from({ length: Math.max(args.throughMonth, 0) }, (_, i) => i + 1);
  const byId = new Map(employees.map((e) => [e.userId, e]));
  const byUserMonth = hoursByUserMonth(args.breakdown.classified);
  const capacityOf = (userId: string, month: number) =>
    byId.get(userId)?.months.find((m) => m.month === month)?.capacity ?? 0;
  const sliceFor = (userIds: string[], ms: number[]) => capacitySlice(byUserMonth, capacityOf, userIds, ms);
  // Teamsummen tar med alle ansatte, som teamYtdRate. En bruker uten kapasitet
  // kan ha godkjente oppgaver, og de timene teller i teamets grad.
  const billableFor = (userId: string | null, ms: number[]) =>
    (userId == null ? args.employees : employees)
      .filter((e) => userId == null || e.userId === userId)
      .reduce((sum, e) => sum + e.months.filter((m) => ms.includes(m.month)).reduce((s, m) => s + m.billableHours, 0), 0);
  const team = sliceFor(employees.map((e) => e.userId), months);
  const billable = billableFor(null, months);
  return {
    year: args.year,
    months,
    target: args.target,
    employees,
    byUserMonth,
    breakdown: args.breakdown,
    team,
    billable,
    rate: args.teamRate,
    sliceFor,
    billableFor,
    nameOf: (userId) => byId.get(userId)?.fullName ?? 'Ukjent',
  };
}

/** Grad når alt som pågår eller venter blir godkjent, med førte timer som anslag. */
export const rateWithPending = (billable: number, slice: CapacitySlice) =>
  calcBillingRate(billable + slice.venter, slice.capacity);

export function periodLabel(model: TeamFaktModel) {
  const last = model.months[model.months.length - 1];
  return last ? `januar til ${MONTH_NAMES_NB[last - 1].toLowerCase()}` : 'ingen avstemte måneder';
}

export function statusLabel(status: string | undefined) {
  switch (status) {
    case 'not_started': return 'ikke startet';
    case 'in_progress': return 'pågår';
    case 'completed': return 'ferdig, venter på godkjenning';
    case 'rejected': return 'avvist';
    default: return 'ukjent status';
  }
}

export interface EntriesFilter {
  segment: CapacityCategory;
  months?: number[];
  userId?: string;
  projectId?: string;
  assignmentId?: string;
}


export const dayName = (iso: string) => {
  const s = new Date(`${iso}T00:00:00Z`).toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Førte timer i en kategori, for ansatte med kapasitet, avgrenset av filteret. */
export function filterEntries(model: TeamFaktModel, f: EntriesFilter) {
  const ids = new Set(model.employees.map((e) => e.userId));
  const months = monthsOf(model, f.months);
  return model.breakdown.classified
    .filter((c) => c.category === f.segment && ids.has(c.userId) && months.includes(c.month))
    .filter((c) => !f.userId || c.userId === f.userId)
    .filter((c) => !f.projectId || c.projectId === f.projectId)
    .filter((c) => !f.assignmentId || c.assignment?.id === f.assignmentId)
    .sort((a, b) => a.date.localeCompare(b.date) || b.hours - a.hours);
}
